import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  MapPin,
  Play,
  RefreshCw,
  Route,
  Sparkles,
} from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const inputClass =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 shadow-sm outline-none transition-colors ring-brand-500/20 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30';
const textareaClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition-colors ring-brand-500/20 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30';
const selectClass = inputClass;
const btnPrimaryClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-600/20 transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondaryClass =
  'inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';
const btnDarkClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-slate-900/15 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50';
const fieldLabelClass = 'mb-1.5 block text-sm font-medium text-slate-800';

const sectionShell =
  'glass-panel overflow-hidden rounded-2xl border border-slate-200/90 p-5 shadow-sm ring-1 ring-slate-900/[0.04] sm:p-6';

type Accent = 'amber' | 'violet' | 'sky' | 'emerald';

const accentBar: Record<Accent, string> = {
  amber: 'from-amber-500 to-orange-400',
  violet: 'from-violet-500 to-purple-500',
  sky: 'from-sky-500 to-cyan-500',
  emerald: 'from-emerald-500 to-teal-500',
};

const accentIconWrap: Record<Accent, string> = {
  amber: 'bg-amber-100 text-amber-800 ring-amber-200/80',
  violet: 'bg-violet-100 text-violet-800 ring-violet-200/80',
  sky: 'bg-sky-100 text-sky-800 ring-sky-200/80',
  emerald: 'bg-emerald-100 text-emerald-800 ring-emerald-200/80',
};

function CronSection(props: {
  accent: Accent;
  icon: React.ElementType;
  title: string;
  description: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { accent, icon: Icon, title, description, badge, actions, children } = props;
  return (
    <section className={sectionShell}>
      <div
        className={`mb-5 h-1 w-full rounded-full bg-gradient-to-r ${accentBar[accent]}`}
        aria-hidden
      />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-1 gap-4">
          <div
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-inset ${accentIconWrap[accent]}`}
          >
            <Icon className="h-6 w-6" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">{title}</h2>
              {badge}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">{description}</p>
          </div>
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">{actions}</div> : null}
      </div>
      <div className="border-t border-slate-100 pt-5">{children}</div>
    </section>
  );
}

function StatTile(props: {
  label: string;
  value: React.ReactNode;
  tone: 'slate' | 'emerald' | 'amber' | 'rose';
}) {
  const { label, value, tone } = props;
  const tones = {
    slate: 'border-slate-200/90 bg-gradient-to-br from-slate-50/80 to-white text-slate-900',
    emerald: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50/70 to-white text-emerald-900',
    amber: 'border-amber-200/80 bg-gradient-to-br from-amber-50/70 to-white text-amber-950',
    rose: 'border-rose-200/80 bg-gradient-to-br from-rose-50/70 to-white text-rose-900',
  };
  return (
    <div
      className={`rounded-xl border px-3 py-3 shadow-sm ring-1 ring-slate-900/[0.03] sm:px-4 ${tones[tone]}`}
    >
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs">{label}</p>
      <p className="mt-1 text-lg font-bold tabular-nums sm:text-xl">{value}</p>
    </div>
  );
}

type ExpiryConfig = {
  sendAtHour: number;
  sendAtMinute: number;
  daysBeforeExpiry: number;
  recipientEmails: string[];
  isActive: boolean;
};

type ExpiryLog = {
  id: string;
  sentAt: string;
  summary: string | null;
};

type NotifyFrequency = 'OFF' | 'IMMEDIATE' | 'DAILY';

type NotifyConfig = {
  frequency: NotifyFrequency;
  sendAtHour: number;
  sendAtMinute: number;
  recipientEmails: string[];
  isActive: boolean;
} | null;

export default function CronJobsPage() {
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const { t } = useTranslation();
  const location = useLocation();

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [expiryLoading, setExpiryLoading] = useState(true);
  const [expirySaving, setExpirySaving] = useState(false);
  const [expiryCfg, setExpiryCfg] = useState<ExpiryConfig | null>(null);
  const [expiryHourInput, setExpiryHourInput] = useState('');
  const [expiryMinuteInput, setExpiryMinuteInput] = useState('');
  const [expiryDaysInput, setExpiryDaysInput] = useState('');
  const [expiryRecipientsText, setExpiryRecipientsText] = useState('');
  const [latestExpiryLog, setLatestExpiryLog] = useState<ExpiryLog | null>(null);

  const [routeSyncLoading, setRouteSyncLoading] = useState(false);
  const [routeSyncRunning, setRouteSyncRunning] = useState(false);
  const [routeSyncStatus, setRouteSyncStatus] = useState<any | null>(null);

  const [addressLoading, setAddressLoading] = useState(false);
  const [addressRunning, setAddressRunning] = useState(false);
  const [addressStatus, setAddressStatus] = useState<any | null>(null);

  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);
  const [notifyCfg, setNotifyCfg] = useState<NotifyConfig>(null);
  const [notifyRecipientsText, setNotifyRecipientsText] = useState('');

  const parseEmailList = (raw: string) =>
    raw
      .split(/[\n,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      setNotice(null);
      setExpiryLoading(true);
      try {
        const [cfgRes, logsRes] = await Promise.all([
          globalAuthorizedFetch(`${API_BASE}/expiry-alert/config`),
          globalAuthorizedFetch(`${API_BASE}/expiry-alert/logs?page=1&pageSize=1`),
        ]);
        if (cancelled) return;
        if (cfgRes.ok) {
          const c: ExpiryConfig = await cfgRes.json();
          setExpiryCfg(c);
          setExpiryHourInput(String(c.sendAtHour ?? 0));
          setExpiryMinuteInput(String(c.sendAtMinute ?? 0));
          setExpiryDaysInput(String(c.daysBeforeExpiry ?? 1));
          setExpiryRecipientsText((c.recipientEmails || []).join('\n'));
        }
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setLatestExpiryLog(logsData?.items?.[0] ?? null);
        }
      } catch {
        if (!cancelled) setError('Failed to load cron jobs data');
      } finally {
        if (!cancelled) setExpiryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalAuthorizedFetch]);

  const loadRouteSync = React.useCallback(async () => {
    setRouteSyncLoading(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/delivery-pricing/recompute-progress`);
      if (!res.ok) throw new Error();
      setRouteSyncStatus(await res.json());
    } catch {
      setRouteSyncStatus(null);
    } finally {
      setRouteSyncLoading(false);
    }
  }, [globalAuthorizedFetch]);

  const loadAddressStatus = React.useCallback(async () => {
    setAddressLoading(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/address-verification/admin/status`);
      if (!res.ok) throw new Error();
      setAddressStatus(await res.json());
    } catch {
      setAddressStatus(null);
    } finally {
      setAddressLoading(false);
    }
  }, [globalAuthorizedFetch]);

  const loadNotifyConfig = React.useCallback(async () => {
    setNotifyLoading(true);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/notifications/config`);
      if (!res.ok) throw new Error();
      const cfg = (await res.json()) as NotifyConfig;
      setNotifyCfg(cfg);
      setNotifyRecipientsText((cfg?.recipientEmails || []).join('\n'));
    } catch {
      setNotifyCfg(null);
    } finally {
      setNotifyLoading(false);
    }
  }, [globalAuthorizedFetch]);

  useEffect(() => {
    void loadRouteSync();
    void loadAddressStatus();
    void loadNotifyConfig();
  }, [loadAddressStatus, loadNotifyConfig, loadRouteSync]);

  const saveExpiry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!expiryCfg) return;
    const parsedHour = Number(expiryHourInput);
    const parsedMinute = Number(expiryMinuteInput);
    const parsedDays = Number(expiryDaysInput);
    if (
      !Number.isFinite(parsedHour) ||
      parsedHour < 0 ||
      parsedHour > 23 ||
      !Number.isFinite(parsedMinute) ||
      parsedMinute < 0 ||
      parsedMinute > 59 ||
      !Number.isFinite(parsedDays) ||
      parsedDays < 1 ||
      parsedDays > 365
    ) {
      setError('Please enter valid numbers: hour 0-23, minute 0-59, days 1-365.');
      return;
    }
    setExpirySaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/expiry-alert/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...expiryCfg,
          sendAtHour: parsedHour,
          sendAtMinute: parsedMinute,
          daysBeforeExpiry: parsedDays,
          recipientEmails: parseEmailList(expiryRecipientsText),
        }),
      });
      if (!res.ok) throw new Error();
      const next = (await res.json()) as ExpiryConfig;
      setExpiryCfg(next);
      setExpiryHourInput(String(next.sendAtHour ?? 0));
      setExpiryMinuteInput(String(next.sendAtMinute ?? 0));
      setExpiryDaysInput(String(next.daysBeforeExpiry ?? 1));
      setExpiryRecipientsText((next.recipientEmails || []).join('\n'));
      setNotice('Expiry alerts cron config saved.');
    } catch {
      setError('Unable to save expiry alerts cron config.');
    } finally {
      setExpirySaving(false);
    }
  };

  const runRouteResync = async () => {
    setRouteSyncRunning(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/delivery-pricing/recompute-all`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotice(`Route resync queued: ${data.queued}/${data.customers}`);
      await loadRouteSync();
    } catch {
      setError('Unable to queue route resync.');
    } finally {
      setRouteSyncRunning(false);
    }
  };

  const runAddressVerification = async () => {
    setAddressRunning(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/address-verification/admin/requeue-all`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNotice(`Address verification queued: ${data.queued}/${data.profiles}`);
      await loadAddressStatus();
    } catch {
      setError('Unable to queue address verification jobs.');
    } finally {
      setAddressRunning(false);
    }
  };

  const saveNotify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!notifyCfg) return;
    setNotifySaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/notifications/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...notifyCfg,
          recipientEmails: parseEmailList(notifyRecipientsText),
        }),
      });
      if (!res.ok) throw new Error();
      const next = (await res.json()) as NotifyConfig;
      setNotifyCfg(next);
      setNotifyRecipientsText((next?.recipientEmails || []).join('\n'));
      setNotice('Order summary cron config saved.');
    } catch {
      setError('Unable to save order summary cron config.');
    } finally {
      setNotifySaving(false);
    }
  };

  const cronCountLabel = useMemo(() => {
    let count = 0;
    if (expiryCfg) count += 1;
    count += 2;
    if (notifyCfg !== null) count += 1;
    return count;
  }, [expiryCfg, notifyCfg]);

  const expiryActiveBadge = expiryCfg?.isActive ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
      {t('common.active', { defaultValue: 'Active' })}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/80">
      {t('common.paused', { defaultValue: 'Paused' })}
    </span>
  );

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-brand-50/40 p-5 shadow-md ring-1 ring-slate-900/[0.04] sm:p-6">
          <div
            className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-brand-400/15 blur-2xl"
            aria-hidden
          />
          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-600 text-white shadow-lg shadow-brand-600/25">
                <Clock className="h-6 w-6" aria-hidden />
              </div>
              <div>
                <h1 className="workspace-page-title">
                  {t('cron_jobs_title', { defaultValue: 'Cron jobs' })}
                </h1>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600">
                  {t('cron_jobs_subtitle', {
                    defaultValue:
                      'Configure schedules and run operational background jobs from one place.',
                  })}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 rounded-xl border border-brand-200/60 bg-white/90 px-4 py-3 shadow-sm backdrop-blur-sm">
              <Sparkles className="h-5 w-5 text-brand-600" aria-hidden />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  {t('cron_jobs_areas', { defaultValue: 'Job areas' })}
                </p>
                <p className="text-xl font-bold tabular-nums text-slate-900">{cronCountLabel}</p>
              </div>
            </div>
          </div>
        </section>

        {error ? (
          <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 to-white px-4 py-3 text-sm text-rose-800 shadow-sm ring-1 ring-rose-900/5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}
        {notice ? (
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-4 py-3 text-sm text-emerald-800 shadow-sm ring-1 ring-emerald-900/5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
            <span>{notice}</span>
          </div>
        ) : null}

        <CronSection
          accent="amber"
          icon={CalendarClock}
          title={t('cron_expiry_alerts_title', { defaultValue: 'Expiry alerts' })}
          description={t('cron_expiry_alerts_desc', {
            defaultValue:
              'Daily email digest for stock nearing expiry — send time, lead window, and recipients.',
          })}
          badge={
            !expiryLoading && expiryCfg ? (
              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {t('common.config', { defaultValue: 'Config' })}
              </span>
            ) : null
          }
        >
          {expiryLoading || !expiryCfg ? (
            <p className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin text-brand-600" aria-hidden />
              {t('common.loading')}
            </p>
          ) : (
            <form onSubmit={saveExpiry} className="grid max-w-3xl gap-5">
              <div className="flex flex-wrap items-center gap-2">{expiryActiveBadge}</div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label>
                  <span className={fieldLabelClass}>{t('common.hour', { defaultValue: 'Hour' })}</span>
                  <input
                    className={inputClass}
                    type="text"
                    inputMode="numeric"
                    value={expiryHourInput}
                    onChange={(e) => setExpiryHourInput(e.target.value)}
                  />
                </label>
                <label>
                  <span className={fieldLabelClass}>{t('common.minute', { defaultValue: 'Minute' })}</span>
                  <input
                    className={inputClass}
                    type="text"
                    inputMode="numeric"
                    value={expiryMinuteInput}
                    onChange={(e) => setExpiryMinuteInput(e.target.value)}
                  />
                </label>
              </div>
              <label>
                <span className={fieldLabelClass}>
                  {t('days_before_expiry', { defaultValue: 'Days before expiry' })}
                </span>
                <input
                  className={inputClass}
                  type="text"
                  inputMode="numeric"
                  value={expiryDaysInput}
                  onChange={(e) => setExpiryDaysInput(e.target.value)}
                />
              </label>
              <label>
                <span className={fieldLabelClass}>
                  {t('common.recipients', { defaultValue: 'Recipients' })}
                </span>
                <textarea
                  className={textareaClass}
                  rows={4}
                  value={expiryRecipientsText}
                  onChange={(e) => setExpiryRecipientsText(e.target.value)}
                  placeholder={t('cron_recipients_placeholder', {
                    defaultValue: 'one@example.com, ops@example.com',
                  })}
                />
              </label>
              <label className="inline-flex w-fit cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm font-medium text-slate-800 transition hover:bg-slate-100/80 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500/40">
                <input
                  type="checkbox"
                  checked={expiryCfg.isActive}
                  onChange={(e) => setExpiryCfg({ ...expiryCfg, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                {t('cron_enabled', { defaultValue: 'Cron enabled' })}
              </label>
              <div className="flex flex-col gap-3 border-t border-slate-100 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <button type="submit" disabled={expirySaving} className={btnPrimaryClass}>
                  {expirySaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <div className="flex items-center gap-2 text-xs text-slate-500 sm:text-sm">
                  <Mail className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  <span>
                    Last run:{' '}
                    <span className="font-medium text-slate-700">
                      {latestExpiryLog?.sentAt
                        ? new Date(latestExpiryLog.sentAt).toLocaleString()
                        : 'No runs yet'}
                    </span>
                  </span>
                </div>
              </div>
            </form>
          )}
        </CronSection>

        <CronSection
          accent="violet"
          icon={Route}
          title={t('cron_resync_routes_title', { defaultValue: 'Resync all customer routes' })}
          description="Queues driving-time recomputation for all customers. Use after large delivery-pricing or geography changes."
          actions={
            <button
              type="button"
              onClick={() => void runRouteResync()}
              disabled={routeSyncRunning}
              className={btnDarkClass}
            >
              {routeSyncRunning ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Play className="h-4 w-4" aria-hidden />
              )}
              {routeSyncRunning ? 'Queueing…' : 'Run now'}
            </button>
          }
        >
          <button
            type="button"
            onClick={() => void loadRouteSync()}
            disabled={routeSyncLoading}
            className={`${btnSecondaryClass} mb-4`}
          >
            <RefreshCw className={`h-4 w-4 ${routeSyncLoading ? 'animate-spin' : ''}`} aria-hidden />
            {routeSyncLoading ? 'Refreshing…' : 'Refresh status'}
          </button>
          {routeSyncStatus ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
              <StatTile label={t('common.total', { defaultValue: 'Total' })} value={routeSyncStatus.totalCustomers} tone="slate" />
              <StatTile label={t('common.done', { defaultValue: 'Done' })} value={routeSyncStatus.doneCount} tone="emerald" />
              <StatTile label={t('common.pending', { defaultValue: 'Pending' })} value={routeSyncStatus.pendingCount} tone="amber" />
              <StatTile label={t('common.failed', { defaultValue: 'Failed' })} value={routeSyncStatus.failedCount} tone="rose" />
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-sm text-slate-500">
              No route sync status available.
            </p>
          )}
        </CronSection>

        <CronSection
          accent="sky"
          icon={MapPin}
          title="Address verification"
          description="Requeues verification for all customer profile addresses and shows queue totals."
          actions={
            <button
              type="button"
              onClick={() => void runAddressVerification()}
              disabled={addressRunning}
              className={btnDarkClass}
            >
              {addressRunning ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Play className="h-4 w-4" aria-hidden />
              )}
              {addressRunning ? 'Queueing…' : 'Run now'}
            </button>
          }
        >
          <button
            type="button"
            onClick={() => void loadAddressStatus()}
            disabled={addressLoading}
            className={`${btnSecondaryClass} mb-4`}
          >
            <RefreshCw className={`h-4 w-4 ${addressLoading ? 'animate-spin' : ''}`} aria-hidden />
            {addressLoading ? 'Refreshing…' : 'Refresh status'}
          </button>
          {addressStatus ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
              <StatTile label="Profiles" value={addressStatus.totalProfiles} tone="slate" />
              <StatTile label="Verified" value={addressStatus.byStatus?.VERIFIED ?? 0} tone="emerald" />
              <StatTile label="Pending" value={addressStatus.byStatus?.PENDING ?? 0} tone="amber" />
              <StatTile label="Failed" value={addressStatus.byStatus?.FAILED ?? 0} tone="rose" />
            </div>
          ) : (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-sm text-slate-500">
              No address verification status available.
            </p>
          )}
        </CronSection>

        <CronSection
          accent="emerald"
          icon={Bell}
          title="Order summary notifications"
          description="Daily cron and immediate alert behavior for SalesNotificationsCron — frequency, schedule, and recipients."
          badge={
            !notifyLoading && notifyCfg ? (
              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Daily cron
              </span>
            ) : null
          }
        >
          {notifyLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin text-brand-600" aria-hidden />
              {t('common.loading')}
            </p>
          ) : !notifyCfg ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-sm text-slate-500">
              No access to order notification cron configuration.
            </p>
          ) : (
            <form onSubmit={saveNotify} className="grid max-w-3xl gap-5">
              <div className="flex flex-wrap items-center gap-2">
                {notifyCfg.isActive ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/80">
                    Paused
                  </span>
                )}
              </div>
              <label>
                <span className={fieldLabelClass}>Frequency</span>
                <select
                  className={selectClass}
                  value={notifyCfg.frequency}
                  onChange={(e) =>
                    setNotifyCfg({ ...notifyCfg, frequency: e.target.value as NotifyFrequency })
                  }
                >
                  <option value="OFF">OFF</option>
                  <option value="IMMEDIATE">IMMEDIATE</option>
                  <option value="DAILY">DAILY</option>
                </select>
              </label>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label>
                  <span className={fieldLabelClass}>Hour</span>
                  <input
                    className={inputClass}
                    type="number"
                    min={0}
                    max={23}
                    value={notifyCfg.sendAtHour}
                    onChange={(e) =>
                      setNotifyCfg({ ...notifyCfg, sendAtHour: Number(e.target.value) })
                    }
                  />
                </label>
                <label>
                  <span className={fieldLabelClass}>Minute</span>
                  <input
                    className={inputClass}
                    type="number"
                    min={0}
                    max={59}
                    value={notifyCfg.sendAtMinute}
                    onChange={(e) =>
                      setNotifyCfg({ ...notifyCfg, sendAtMinute: Number(e.target.value) })
                    }
                  />
                </label>
              </div>
              <label>
                <span className={fieldLabelClass}>Recipients</span>
                <textarea
                  className={textareaClass}
                  rows={4}
                  value={notifyRecipientsText}
                  onChange={(e) => setNotifyRecipientsText(e.target.value)}
                  placeholder="one@example.com"
                />
              </label>
              <label className="inline-flex w-fit cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm font-medium text-slate-800 transition hover:bg-slate-100/80 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500/40">
                <input
                  type="checkbox"
                  checked={notifyCfg.isActive}
                  onChange={(e) => setNotifyCfg({ ...notifyCfg, isActive: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                Cron enabled
              </label>
              <button type="submit" disabled={notifySaving} className={`${btnPrimaryClass} w-fit`}>
                {notifySaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </form>
          )}
        </CronSection>

        <CronSection
          accent="violet"
          icon={Mail}
          title={t('backoffice.cron_referral_title', { defaultValue: 'Referral announcement emails' })}
          description={t('backoffice.cron_referral_desc', {
            defaultValue:
              'Backend cron runs every minute. When the clock matches the time you set under Orders → Referrals, it emails verified customers who have never been referred and have not received this campaign. Uses mail purpose REFERRAL for sender routing.',
          })}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <StatTile
              label={t('backoffice.cron_referral_tile_schedule', { defaultValue: 'Schedule' })}
              value={t('backoffice.cron_referral_tile_schedule_val', {
                defaultValue: 'Configured on Referrals workspace page',
              })}
              tone="slate"
            />
            <StatTile
              label={t('backoffice.cron_referral_tile_skip', { defaultValue: 'Skips' })}
              value={t('backoffice.cron_referral_tile_skip_val', {
                defaultValue: 'Already referred · already emailed',
              })}
              tone="emerald"
            />
          </div>
          <p className="mt-4 text-xs text-slate-500">
            {t('backoffice.cron_referral_note', {
              defaultValue:
                'Requires referral program active + announcement enabled. Batches 80 recipients per run; idempotent per calendar slot.',
            })}
          </p>
        </CronSection>
      </main>
    </MainLayout>
  );
}
